import React, { useState, useEffect } from 'react';
import numeral from 'numeral';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setPortal } from 'modules/portal';

import { makeStyles } from '@material-ui/core/styles';
import { userOnLogin } from 'lib/api/user';

import { FluctuationStatisticsData } from 'lib/api/report';
import NewTableContainer from 'components/molecules/NewTable';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import SortLabelWrap from 'components/molecules/MbTable/SortLabelWrap';
import SpanningTableContents from 'components/molecules/MbTable/SpanningTableContents';
import NewButton from 'components/molecules/NewButton';

import FluctuationGraph from './FluctuationGraph';

const useStyles = makeStyles((theme) => ({
  rafColor: {
    color: '#000',
  },
}));

const FluctuationStatistics = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));

  const [initialization, setInitialization] = useState(false);

  const initParams = {
    sort: 'desc',
    order: 'ADVRTS_AMT',
    sDate: moment().add(-2, 'day').format('YYYYMMDD'),
    eDate: moment().add(-2, 'day').format('YYYYMMDD'),
    diffSDate: moment().add(-1, 'day').format('YYYYMMDD'),
    diffEDate: moment().add(-1, 'day').format('YYYYMMDD'),
    pltfomTpCode: 'all',
    itlTpCode: 'all',
  };

  const [params, setParams] = useState(initParams);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    setInitialization(true);
    setParams(initParams);
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  const handleSortChange = (orderKey) => {
    const { order, sort } = params;
    const isDesc = order === orderKey && sort === 'desc';
    const converSort = isDesc ? 'asc' : 'desc';
    setParams({
      ...params,
      order: orderKey,
      sort: converSort,
    });
  };

  const makeRafClass = (row) => {
    let className;
    if (row === 0) return classes.rafColor;

    const checkRow = row * 100;

    if (checkRow > 100) className = classes.rafUpperColor;
    else if (checkRow < 100) {
      if (checkRow < 80) className = 'purple';
      else className = 'red';
    } else {
      className = classes.rafColor;
    }

    return className;
  };

  // header 및 body 컬럼 커스텀
  const customized = {
    ADVRTS_AMT: {
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle={t('지출금액')} type="head">
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('기준일')}
                orderKey="ADVRTS_AMT"
                onClick={(e) => handleSortChange('ADVRTS_AMT')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('비교일')}
                orderKey="DIFF_ADVRTS_AMT"
                onClick={(e) => handleSortChange('DIFF_ADVRTS_AMT')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('증감')}
                orderKey="RAF_ADVRTS_AMT"
                onClick={(e) => handleSortChange('RAF_ADVRTS_AMT')}
              />
            </div>
          </SpanningTableContents>
        );
        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['ADVRTS_AMT']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['DIFF_ADVRTS_AMT']).format('#,##0')}
            </div>
            <div
              style={{ width: 100 }}
              className={makeRafClass(rowObj['RAF_RATE_ADVRTS_AMT'])}
            >
              {numeral(rowObj['RAF_ADVRTS_AMT']).format('#,##0')}
              <br />({numeral(rowObj['RAF_RATE_ADVRTS_AMT']).format('0.0%')})
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
    },
    DIFF_ADVRTS_AMT: {
      merged: true,
    },
    RAF_ADVRTS_AMT: {
      merged: true,
    },
    RAF_RATE_ADVRTS_AMT: {
      merged: true,
    },
    PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle={t('노출')} type="head">
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('기준일')}
                orderKey="PAR_EPRS_CNT"
                onClick={(e) => handleSortChange('PAR_EPRS_CNT')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('비교일')}
                orderKey="DIFF_PAR_EPRS_CNT"
                onClick={(e) => handleSortChange('DIFF_PAR_EPRS_CNT')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('증감')}
                orderKey="RAF_PAR_EPRS_CNT"
                onClick={(e) => handleSortChange('RAF_PAR_EPRS_CNT')}
              />
            </div>
          </SpanningTableContents>
        );

        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['PAR_EPRS_CNT']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['DIFF_PAR_EPRS_CNT']).format('#,##0')}
            </div>
            <div
              style={{ width: 100 }}
              className={makeRafClass(rowObj['RAF_RATE_PAR_EPRS_CNT'])}
            >
              {numeral(rowObj['RAF_PAR_EPRS_CNT']).format('#,##0')}
              <br />({numeral(rowObj['RAF_RATE_PAR_EPRS_CNT']).format('0.0%')})
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: {
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderRight: '1px solid #e5e8eb',
      },
    },
    DIFF_PAR_EPRS_CNT: {
      merged: true,
    },
    RAF_PAR_EPRS_CNT: {
      merged: true,
    },
    RAF_RATE_PAR_EPRS_CNT: {
      merged: true,
    },
    CLICK_CNT: {
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle={t('클릭')} type="head">
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('기준일')}
                orderKey="CLICK_CNT"
                onClick={(e) => handleSortChange('CLICK_CNT')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('비교일')}
                orderKey="DIFF_CLICK_CNT"
                onClick={(e) => handleSortChange('DIFF_CLICK_CNT')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('증감')}
                orderKey="RAF_CLICK_CNT"
                onClick={(e) => handleSortChange('RAF_CLICK_CNT')}
              />
            </div>
          </SpanningTableContents>
        );
        result.orderFlag = false;
        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['CLICK_CNT']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['DIFF_CLICK_CNT']).format('#,##0')}
            </div>
            <div
              style={{ width: 100 }}
              className={makeRafClass(rowObj['RAF_RATE_CLICK_CNT'])}
            >
              {numeral(rowObj['RAF_CLICK_CNT']).format('#,##0')}
              <br />({numeral(rowObj['RAF_RATE_CLICK_CNT']).format('0.0%')})
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: {
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderRight: '1px solid #e5e8eb',
      },
    },
    DIFF_CLICK_CNT: {
      merged: true,
    },
    RAF_CLICK_CNT: {
      merged: true,
    },
    RAF_RATE_CLICK_CNT: {
      merged: true,
    },
    CLICK_RATE: {
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle="CTR" type="head">
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('기준일')}
                orderKey="CLICK_RATE"
                onClick={(e) => handleSortChange('CLICK_RATE')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('비교일')}
                orderKey="DIFF_CLICK_RATE"
                onClick={(e) => handleSortChange('DIFF_CLICK_RATE')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('증감')}
                orderKey="RAF_CLICK_RATE"
                onClick={(e) => handleSortChange('RAF_CLICK_RATE')}
              />
            </div>
          </SpanningTableContents>
        );
        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['CLICK_RATE']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['DIFF_CLICK_RATE']).format('#,##0')}
            </div>
            <div
              style={{ width: 100 }}
              className={makeRafClass(rowObj['RAF_RATE_CLICK_RATE'])}
            >
              {numeral(rowObj['RAF_CLICK_RATE']).format('#,##0')}
              <br />({numeral(rowObj['RAF_RATE_CLICK_RATE']).format('0.0%')})
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: {
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderRight: '1px solid #e5e8eb',
      },
    },
    DIFF_CLICK_RATE: {
      merged: true,
    },
    RAF_CLICK_RATE: {
      merged: true,
    },
    RAF_RATE_CLICK_RATE: {
      merged: true,
    },
    SESSION_ORDER_AMT: {
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle={t('세션매출')} type="head">
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('기준일')}
                orderKey="SESSION_ORDER_AMT"
                onClick={(e) => handleSortChange('SESSION_ORDER_AMT')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('비교일')}
                orderKey="DIFF_SESSION_ORDER_AMT"
                onClick={(e) => handleSortChange('DIFF_SESSION_ORDER_AMT')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('증감')}
                orderKey="RAF_SESSION_ORDER_AMT"
                onClick={(e) => handleSortChange('RAF_SESSION_ORDER_AMT')}
              />
            </div>
          </SpanningTableContents>
        );
        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['SESSION_ORDER_AMT']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['DIFF_SESSION_ORDER_AMT']).format('#,##0')}
            </div>
            <div
              style={{ width: 100 }}
              className={makeRafClass(rowObj['RAF_RATE_SESSION_ORDER_AMT'])}
            >
              {numeral(rowObj['RAF_SESSION_ORDER_AMT']).format('#,##0')}
              <br />(
              {numeral(rowObj['RAF_RATE_SESSION_ORDER_AMT']).format('0.0%')})
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: {
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderRight: '1px solid #e5e8eb',
      },
    },
    DIFF_SESSION_ORDER_AMT: {
      merged: true,
    },
    RAF_SESSION_ORDER_AMT: {
      merged: true,
    },
    RAF_RATE_SESSION_ORDER_AMT: {
      merged: true,
    },
    MOB_SESSION_ROAS: {
      orderFlag: false,
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle={t('세션매출(모비온)')} type="head">
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('기준일')}
                orderKey="MOB_SESSION_ROAS"
                onClick={(e) => handleSortChange('MOB_SESSION_ROAS')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('비교일')}
                orderKey="DIFF_MOB_SESSION_ROAS"
                onClick={(e) => handleSortChange('DIFF_MOB_SESSION_ROAS')}
              />
            </div>
            <div style={{ width: 100 }}>
              <SortLabelWrap
                params={params}
                label={t('증감')}
                orderKey="RAF_MOB_SESSION_ROAS"
                onClick={(e) => handleSortChange('RAF_MOB_SESSION_ROAS')}
              />
            </div>
          </SpanningTableContents>
        );

        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['MOB_SESSION_ROAS']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['DIFF_MOB_SESSION_ROAS']).format('#,##0')}
            </div>
            <div
              style={{ width: 100 }}
              className={makeRafClass(rowObj['RAF_RATE_MOB_SESSION_ROAS'])}
            >
              {numeral(rowObj['RAF_MOB_SESSION_ROAS']).format('#,##0')}
              <br />(
              {numeral(rowObj['RAF_RATE_MOB_SESSION_ROAS']).format('0.0%')})
            </div>
          </SpanningTableContents>
        );
      },
    },
    DIFF_MOB_SESSION_ROAS: {
      merged: true,
    },
    RAF_MOB_SESSION_ROAS: {
      merged: true,
    },
    RAF_RATE_MOB_SESSION_ROAS: {
      merged: true,
    },
  };

  const addColumnList = (() => {
    const result = new Map();
    result.set(0, {
      id: 'MANAGE_ICONS',
      label: t('관리'),
      align: 'center',
      makeRowFunc: (data) => {
        return (
          <NewButton
            title={t('광고주 로그인')}
            icon="faSignInAlt"
            iconBtn
            onClick={(e) => handleOnLogin(data['ADVER_ID'])}
            disableRipple
          />
        );
      },
    });
    return result;
  })();

  /**
   * 로그인처리
   */
  const handleOnLogin = (ADVER_ID) => {
    userOnLogin(ADVER_ID, 'manage')
      .then((response) => {
        const { data, success } = response.data;
        if (success) {
          handlePortalUrl(data.url);
        } else {
          alert('로그인에 실패했습니다. 관리자에게 문의하세요.');
        }
      })
      .catch((error) => {
        console.log('error', error);
      });
  };

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = {
      url: url,
      cnt: popIndex,
    };

    dispatch(setPortal(newPortal));
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          useDiffDate
        />
      </NewFilterWrap>
      <FluctuationGraph params={params} />
      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={FluctuationStatisticsData}
        customized={customized}
        addColumnList={addColumnList}
      />
    </>
  );
};

export default FluctuationStatistics;
